import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { useDialog } from "../../lib/dialog";
import { useSettings } from "../../lib/settings";
import { useEvents } from "../../lib/events";
import AdminLayout from "../../components/app/AdminLayout";
import { Loader, Card } from "./Dashboard";
import { Plus, Trash2, Users, Pencil, MapPin, Clock, ChevronRight, Layers } from "lucide-react";

const EMPTY = { name: "", date: "", start_time: "10:00", end_time: "14:00", location: "", capacity: 24, type: "audition", groups_enabled: false, group_count: 3 };

export default function Auditions() {
  const dialog = useDialog();
  const { formatDate } = useSettings();
  const { isArchived } = useEvents();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => adminApi.get("/admin/auditions").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (au) => {
    setEditingId(au.id);
    setForm({ name: au.name, date: au.date, start_time: au.start_time, end_time: au.end_time, location: au.location || "", capacity: au.capacity ?? 24, type: au.type || "audition", groups_enabled: !!au.groups_enabled, group_count: au.group_count || 3 });
    setShowForm(true); window.scrollTo(0, 0);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name || !form.date) return;
    const payload = { ...form, group_count: Math.max(1, parseInt(form.group_count, 10) || 1) };
    if (editingId) await adminApi.patch(`/admin/auditions/${editingId}`, payload);
    else await adminApi.post("/admin/auditions", payload);
    closeForm(); load();
  };

  const del = async (au) => {
    if (await dialog.confirm({ title: "Delete this audition?", description: `Bookings for "${au.name}" will be lost.`, confirmLabel: "Delete", destructive: true })) {
      await adminApi.delete(`/admin/auditions/${au.id}`); load();
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const filtered = items?.filter((au) => (tab === "upcoming" ? au.date >= today : au.date < today)) || [];

  return (
    <AdminLayout title="Auditions" subtitle="Manage audition days, groups, and the on-the-day sign-in board"
      actions={!isArchived && (
        <button onClick={() => (showForm ? closeForm() : openCreate())} data-testid="new-audition-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">
          <Plus size={14} /> New Audition
        </button>
      )}>
      {showForm && (
        <Card title={editingId ? "Edit Audition" : "Create Audition"} className="mb-4">
          <div className="grid md:grid-cols-2 gap-3">
            <Inp label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="audition-name" />
            <Inp label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="audition-date" />
            <Inp label="Start time" type="time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} />
            <Inp label="End time" type="time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} />
            <Inp label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
            <Inp label="Capacity" type="number" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: parseInt(v) || null })} />
            <div>
              <label className="label-mini block mb-1.5">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm">
                <option value="audition">Audition</option>
                <option value="dress_rehearsal">Dress Rehearsal</option>
              </select>
            </div>
            <div className="md:col-span-2 pt-2 border-t border-[#1a1a1a] mt-1">
              <label className="flex items-center gap-2.5 cursor-pointer" data-testid="audition-groups-toggle">
                <input type="checkbox" checked={form.groups_enabled} onChange={(e) => setForm({ ...form, groups_enabled: e.target.checked })} className="accent-blood w-4 h-4" />
                <span className="text-sm text-[#F2EBE1] font-medium">Enable Groups</span>
                <span className="text-xs text-[#6B6862]">Split attendees into balanced groups (e.g. for staggered call times)</span>
              </label>
              {form.groups_enabled && (
                <div className="mt-3 w-48">
                  <Inp label="Number of Groups" type="number" value={form.group_count} onChange={(v) => setForm({ ...form, group_count: v })} testid="audition-group-count" />
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={save} data-testid="audition-save-btn" className="px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">{editingId ? "Save Changes" : "Save"}</button>
            <button onClick={closeForm} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:text-[#F2EBE1]">Cancel</button>
          </div>
        </Card>
      )}

      <div className="flex gap-2 mb-4 border-b border-[#1a1a1a]">
        <TabBtn active={tab === "upcoming"} onClick={() => setTab("upcoming")}>Upcoming</TabBtn>
        <TabBtn active={tab === "past"} onClick={() => setTab("past")}>Past Auditions</TabBtn>
      </div>

      {!items ? <Loader /> : (
        <div className="space-y-2">
          {filtered.length === 0 && <p className="text-sm text-[#6B6862]">No {tab} auditions.</p>}
          {filtered.map((au) => (
            <div key={au.id} onClick={() => navigate(`/admin/auditions/${au.id}`)} data-testid={`audition-card-${au.id}`}
              className="cursor-pointer p-4 border border-[#1a1a1a] bg-[#0A0A0A] hover:border-blood transition-colors flex items-center gap-4">
              <div className="text-center font-mono shrink-0 w-14">
                <div className="text-2xl text-blood leading-none">{new Date(au.date).getDate()}</div>
                <div className="text-[10px] uppercase text-[#6B6862]">{new Date(au.date).toLocaleDateString("en-GB", { month: "short" })}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
                  {au.name}
                  {au.closed && <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-[#262626] text-[#A19D94]">Closed</span>}
                  {au.groups_enabled && <span className="inline-flex items-center gap-1 text-[10px] font-mono normal-case tracking-normal text-[#6B6862]"><Layers size={11} />{au.group_count} groups</span>}
                </div>
                <div className="flex flex-wrap gap-4 mt-1 text-xs text-[#6B6862]">
                  <span className="flex items-center gap-1"><Clock size={11} /> {au.start_time}–{au.end_time}</span>
                  {au.location && <span className="flex items-center gap-1"><MapPin size={11} /> {au.location}</span>}
                  <span className="flex items-center gap-1"><Users size={11} /> {au.booked_count || 0} expected · {au.signed_in_count || 0} signed in</span>
                </div>
              </div>
              {!isArchived && (
                <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => openEdit(au)} data-testid={`audition-edit-${au.id}`} className="text-[#6B6862] hover:text-[#F2EBE1]" title="Edit"><Pencil size={15} /></button>
                  <button onClick={() => del(au)} data-testid={`audition-delete-${au.id}`} className="text-[#6B6862] hover:text-blood" title="Delete"><Trash2 size={15} /></button>
                </div>
              )}
              <ChevronRight size={18} className="text-[#6B6862] shrink-0" />
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

const TabBtn = ({ active, onClick, children }) => (
  <button onClick={onClick} className={`px-4 py-2 text-xs uppercase tracking-wider border-b-2 -mb-px transition-colors ${active ? "border-blood text-[#F2EBE1]" : "border-transparent text-[#6B6862] hover:text-[#A19D94]"}`}>{children}</button>
);

const Inp = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="label-mini block mb-1.5">{label}</label>
    <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
  </div>
);
